import { FacetEngineFilterClause } from './MarketplaceFacetTypes';
import { SourceQueryError } from './SourceRetrievalTypes';
import { WitboostMarketplaceSearchDocument } from './MarketplaceDocumentTypes';

export type MarketplaceKeywordSortValue = string | number | boolean | null;
export type MarketplaceKeywordKind = 'system' | 'component';

export interface MarketplaceKeywordResolvedSort {
  id: string;
  direction: 'asc' | 'desc';
  field?: string;
  valueType: 'relevance' | 'text' | 'number' | 'boolean' | 'date';
  nulls: 'first' | 'last';
  fingerprint: string;
  includeRank: boolean;
}

export interface MarketplaceKeywordKeysetPosition {
  sortValue: MarketplaceKeywordSortValue;
  kind: MarketplaceKeywordKind;
  urn: string;
}

export interface MarketplaceKeywordMatchEvidence {
  field: string;
  headline: string;
}

export interface MarketplaceKeywordHighlightTags {
  preTag: string;
  postTag: string;
}

export type MarketplaceKeywordSelectedDocumentPath =
  | 'documentId'
  | '_computedInfo'
  | 'useCaseTemplateId'
  | 'taxonomy_id'
  | 'kind'
  | '_businessConcepts';

export type MarketplaceKeywordDocumentProjection =
  | { type: 'full' }
  | { type: 'summary' }
  | {
      type: 'selected';
      paths: MarketplaceKeywordSelectedDocumentPath[];
    };

export interface MarketplaceKeywordEngineQuery {
  collatorType: 'marketplace-projects';
  environment: string;
  term: string;
  filters: FacetEngineFilterClause[];
  sort: MarketplaceKeywordResolvedSort;
  limit: number;
  after?: MarketplaceKeywordKeysetPosition;
  useHighlight?: boolean;
  projection: MarketplaceKeywordDocumentProjection;
}

export interface MarketplaceKeywordEngineRow {
  kind: MarketplaceKeywordKind;
  urn: string;
  document: WitboostMarketplaceSearchDocument;
  sortValue: MarketplaceKeywordSortValue;
  rank?: number;
  matchEvidence?: MarketplaceKeywordMatchEvidence[];
}

export interface MarketplaceKeywordTotals {
  items: number;
}

export interface MarketplaceKeywordEngineResponse {
  rows: MarketplaceKeywordEngineRow[];
  totals: MarketplaceKeywordTotals;
  hasMore: boolean;
  highlightTags?: MarketplaceKeywordHighlightTags;
}

export class MarketplaceKeywordEngineError extends SourceQueryError {
  constructor(
    public code:
      | 'unavailable'
      | 'invalid_collator'
      | 'invalid_field'
      | 'invalid_operator'
      | 'query_error'
      | 'internal_error',
    message: string,
  ) {
    super(code, message);
    this.name = 'MarketplaceKeywordEngineError';
  }
}

export interface IMarketplaceKeywordSearchEngine {
  queryMarketplaceKeywordSearch(
    query: MarketplaceKeywordEngineQuery,
  ): Promise<MarketplaceKeywordEngineResponse>;
}
